import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Heart, Search, MessagesSquare, UserRound, Bell, Star,
  Users, ShieldCheck, Sparkles, Settings, Crown, LogOut, Menu, X, HeartHandshake, LifeBuoy, Phone, Radio, ClipboardList, Handshake,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { FullSpinner } from "@/components/States";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/matches", label: "Matches", icon: Heart },
  { to: "/app/discover", label: "Near Me & Online", icon: Search, flag: "PROFILE_DISCOVERY" },
  { to: "/app/search", label: "Search", icon: Search },
  { to: "/app/interests", label: "Interests", icon: HeartHandshake },
  { to: "/app/shortlist", label: "Shortlist", icon: Star },
  { to: "/app/jodi/decision-workspace", label: "My Jodi Workspace", icon: ClipboardList },
  { to: "/app/jodi/understanding", label: "Understanding Hub", icon: Handshake, flag: "JODI_UNDERSTANDING_HUB" },
  { to: "/app/messages", label: "Messages", icon: MessagesSquare },
  { to: "/app/calls", label: "Secure Calls", icon: Phone, flag: "SECURE_CALLS" },
  { to: "/app/broadcast", label: "Profile Broadcast", icon: Radio, flag: "BROADCAST" },
  { to: "/app/events", label: "Match Events", icon: Sparkles, flag: "MATCH_EVENTS" },
  { to: "/app/match-assist", label: "Match Assist", icon: HeartHandshake, flag: "MATCH_ASSIST" },
  { to: "/app/boost", label: "Boost & Spotlight", icon: Crown, flag: "BOOST" },
  { to: "/app/compatibility-lab", label: "Compatibility Lab", icon: Sparkles, flag: "MATCH_COMPARISON" },
  { to: "/app/family", label: "Family Connect", icon: Users, flag: "FAMILY_CONNECT" },
  { to: "/app/referrals", label: "Invite Friends", icon: Users, flag: "REFERRALS" },
  { to: "/app/verification", label: "Trust & Verification", icon: ShieldCheck, flag: "ADVANCED_VERIFICATION" },
  { to: "/app/subscription", label: "Membership", icon: Crown },
  { to: "/app/safety", label: "Safety Center", icon: LifeBuoy },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

const BOTTOM = [
  { to: "/app/dashboard", label: "Home", icon: LayoutDashboard },
  { to: "/app/matches", label: "Matches", icon: Heart },
  { to: "/app/search", label: "Search", icon: Search },
  { to: "/app/messages", label: "Messages", icon: MessagesSquare },
  { to: "/app/profile", label: "Profile", icon: UserRound },
];

export function ProtectedRoute({ children, adminOnly }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (!loading && !user) nav("/login");
    if (!loading && user && adminOnly && !user.is_admin) nav("/app/dashboard");
  }, [user, loading, nav, adminOnly]);
  if (loading) return <div className="min-h-screen"><FullSpinner label="Loading ManaJodi…" /></div>;
  if (!user) return null;
  if (adminOnly && !user.is_admin) return null;
  return children;
}

export function AppLayout() {
  const { user, profile, logout, flags } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [attention, setAttention] = useState(0);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setUnread(data.unread)).catch(() => {});
  }, [loc.pathname]);

  useEffect(() => {
    const fetchAttention = () => api.get("/jodi/workspace/attention-count").then(({ data }) => setAttention(data.count)).catch(() => {});
    fetchAttention();
    const id = setInterval(fetchAttention, 60000);
    window.addEventListener("mj:refresh-attention", fetchAttention);
    return () => { clearInterval(id); window.removeEventListener("mj:refresh-attention", fetchAttention); };
  }, [loc.pathname]);

  const doLogout = () => { logout(); nav("/"); };
  const firstName = (profile?.full_name || "there").split(" ")[0];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Top header */}
      <header className="sticky top-0 z-40 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-[#E8DFD1]">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(true)} data-testid="open-sidebar">
              <Menu className="w-5 h-5 text-[#58111A]" />
            </button>
            <Link to="/app/dashboard" className="font-serif font-bold text-xl text-[#58111A]">
              Mana<span className="text-[#C59B27]">Jodi</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/app/notifications" className="relative p-2 rounded-lg hover:bg-[#FAF3E0]" data-testid="nav-notifications">
              <Bell className="w-5 h-5 text-[#58111A]" />
              {unread > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 text-[10px] font-bold text-white bg-[#BE123C] rounded-full flex items-center justify-center">{unread}</span>}
            </Link>
            <Link to="/app/profile" className="flex items-center gap-2 p-1 pr-2 rounded-full hover:bg-[#FAF3E0]" data-testid="nav-profile-avatar">
              <Avatar photos={profile?.photos} name={firstName} size={32} className="rounded-full" />
              <span className="hidden sm:block text-sm font-medium text-[#261A1C]">{firstName}</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="flex max-w-[1500px] mx-auto">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-[#E8DFD1] min-h-[calc(100vh-57px)] sticky top-[57px] p-3">
          <SidebarNav loc={loc} onNav={() => {}} isAdmin={user?.is_admin} flags={flags} attention={attention} />
          <button onClick={doLogout} className="mt-auto flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[#605255] hover:bg-[#FAF3E0]" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-72 bg-[#FDFBF7] p-3 overflow-y-auto animate-fadeup">
              <div className="flex items-center justify-between mb-3 px-2">
                <span className="font-serif font-bold text-xl text-[#58111A]">Mana<span className="text-[#C59B27]">Jodi</span></span>
                <button onClick={() => setOpen(false)}><X className="w-5 h-5 text-[#58111A]" /></button>
              </div>
              <SidebarNav loc={loc} onNav={() => setOpen(false)} isAdmin={user?.is_admin} flags={flags} attention={attention} />
              <button onClick={doLogout} className="mt-4 flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[#605255] hover:bg-[#FAF3E0] w-full">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0 px-4 sm:px-6 py-6 pb-24 lg:pb-10">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E8DFD1] px-2 lg:hidden flex justify-around items-center shadow-lg" style={{ paddingTop: "0.375rem", paddingBottom: "max(0.375rem, env(safe-area-inset-bottom))" }} data-testid="mobile-bottom-nav">
        {BOTTOM.map(({ to, label, icon: Icon }) => {
          const activePath = to === "/app/profile" ? loc.pathname.startsWith("/app/profile") && !loc.pathname.includes("/profile/") : loc.pathname === to;
          return (
            <Link key={to} to={to} data-testid={`bottom-nav-${label.toLowerCase()}`}
              className={`flex flex-col items-center gap-0.5 px-2 py-1 ${activePath ? "text-[#781826]" : "text-slate-400"}`}>
              <Icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

function SidebarNav({ loc, onNav, isAdmin, flags = {}, attention = 0 }) {
  return (
    <nav className="space-y-0.5">
      {NAV.filter((item) => !item.flag || flags[item.flag] !== false).map(({ to, label, icon: Icon }) => {
        const active = loc.pathname === to || (to !== "/app/dashboard" && loc.pathname.startsWith(to));
        const showBadge = to === "/app/jodi/decision-workspace" && attention > 0;
        return (
          <Link key={to} to={to} onClick={onNav} data-testid={`sidebar-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${active ? "bg-[#781826] text-[#FAF5F0]" : "text-[#605255] hover:bg-[#FAF3E0]"}`}>
            <Icon className="w-4 h-4" /> <span className="flex-1">{label}</span>
            {showBadge && (
              <span data-testid="workspace-attention-badge" className="min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-[#BE123C] rounded-full flex items-center justify-center">
                {attention > 99 ? "99+" : attention}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}
